/**
 * Monta a folha A4: página em branco + unidades carimbadas nos slots
 * ocupados + linhas de corte + overlay (texto/QR) por cima.
 *
 * Origem PDF = canto inferior esquerdo (igual ReportLab / pdf-lib).
 *
 * As units podem carregar paths fora da caixa (crop marks do SEM-TEXTO A4
 * vazam no extract). Por isso o carimbo é clipado à ARTWORK_LAYOUT do slot;
 * as linhas de corte vêm só de drawCutMarks (retas, sem o −0,21 pt legado).
 *
 * Papel "art" (caminho novo, ver composeArtSheet): em vez do template de
 * unidade, cada carta carimba o próprio PNG aprovado (fetchArtPngBytes),
 * escalado à largura da unidade e clipado à caixa da frente com sangria; o
 * verso é pintado de branco e as linhas de corte usam drawCutMarksForBoxes
 * com uma caixa combinada frente+verso por carta — a dobra não é corte, então
 * não leva tick, e o `outset` mantém a cap redonda fora da arte.
 */
import {
  PDFDocument,
  pushGraphicsState,
  popGraphicsState,
  moveTo,
  lineTo,
  closePath,
  clip,
  endPath,
  rgb,
} from "pdf-lib";
import { ARTWORK_LAYOUT } from "./labelAssets";
import { PAGE } from "./layout/constants";
import { artUnitPt, artSlotsPt, ART_PER_PAGE } from "./layout/artGeometryPt";
import { slotForIndex } from "./sheetComposer";
import { fetchUnitTemplateBytes } from "./labelTemplates";
import { fetchArtPngBytes } from "./artPngFetch";
import { drawCutMarks, drawCutMarksForBoxes, CUT_MARK_OUTSET } from "./cutMarks";

const slotBottomLeft = (slot) => ({
  x: ARTWORK_LAYOUT.x + (slot.x_offset || 0),
  y: ARTWORK_LAYOUT.y + (slot.y_offset || 0),
});

/** Clip retangular (origem PDF) antes de carimbar a unit. */
const clipRect = (page, x, y, width, height) => {
  page.pushOperators(
    pushGraphicsState(),
    moveTo(x, y),
    lineTo(x + width, y),
    lineTo(x + width, y + height),
    lineTo(x, y + height),
    closePath(),
    clip(),
    endPath()
  );
};

/** y_top (origem topo, y desce) → y PDF (origem embaixo) para um retângulo de altura `height`. */
const toPdfY = (topY, height) => PAGE.height - topY - height;

/**
 * Ticks curtos: 8 pt saem da caixa combinada e param bem dentro do vão entre
 * unidades (≈ 39,7 pt na horizontal, ≈ 41,4 pt na vertical), sem encostar na
 * unidade vizinha. Os 12 pt do padrão vinham de quando havia tick na dobra.
 */
const ART_TICK_LEN = 8;

/** Caixa `{ left, bottom, right, top }` em pt PDF de um retângulo do slot. */
const trimBoxPt = (slot, trim) => {
  const bottom = toPdfY(slot.y + trim.y, trim.height);
  return {
    left: slot.x + trim.x,
    right: slot.x + trim.x + trim.width,
    bottom,
    top: bottom + trim.height,
  };
};

/**
 * Folha do papel "art": PNG aprovado por carta, clipado à frente com sangria;
 * verso branco; linhas de corte numa única caixa por carta (união do corte da
 * frente com o do verso) — a dobra fica sem tick.
 */
const composeArtSheet = async (out, overlayPages, pageCards, geometry, resolveArtUrl) => {
  const { unit, front, back, frontTrim, backTrim } = artUnitPt(geometry);
  const slots = artSlotsPt(geometry);

  // A URL presigned da arte vale 600 s e é assinada no enriquecimento (ao abrir
  // a tela). Numa sessão de edição longa ela já expirou no clique de gerar, então
  // `resolveArtUrl` reassina — uma vez por artKey neste documento. Se falhar,
  // cai na URL que veio na carta.
  const urlCache = new Map();
  const urlForCard = async (card) => {
    const artKey = card.art?.artKey;
    if (!resolveArtUrl || !artKey) return card.art.url;
    if (!urlCache.has(artKey)) {
      urlCache.set(artKey, (async () => resolveArtUrl(artKey))().catch(() => null));
    }
    return (await urlCache.get(artKey)) || card.art.url;
  };

  // Embute cada PNG no máximo uma vez por URL neste documento.
  const embedCache = new Map();
  const embedArt = async (url) => {
    if (embedCache.has(url)) return embedCache.get(url);
    const promise = fetchArtPngBytes(url)
      .then((bytes) => out.embedPng(bytes))
      .catch((err) => {
        embedCache.delete(url);
        throw err;
      });
    embedCache.set(url, promise);
    return promise;
  };

  for (let pageIndex = 0; pageIndex < overlayPages.length; pageIndex += 1) {
    const page = out.addPage([PAGE.width, PAGE.height]);
    const cardsOnPage = pageCards.slice(pageIndex * ART_PER_PAGE, (pageIndex + 1) * ART_PER_PAGE);
    const boxes = [];

    for (let i = 0; i < cardsOnPage.length; i += 1) {
      const card = cardsOnPage[i];
      const slot = slots[i];
      const png = await embedArt(await urlForCard(card));
      const scale = unit.width / png.width;
      const drawH = png.height * scale;

      // Frente: PNG escalado à largura da unidade, topo no topo da unidade,
      // clipado à caixa da frente com sangria (sobra some no clip).
      clipRect(page, slot.x, toPdfY(slot.y, front.height), front.width, front.height);
      page.drawImage(png, { x: slot.x, y: toPdfY(slot.y, drawH), width: unit.width, height: drawH });
      page.pushOperators(popGraphicsState());

      // Verso: branco.
      page.drawRectangle({
        x: slot.x,
        y: toPdfY(slot.y + back.y, back.height),
        width: back.width,
        height: back.height,
        color: rgb(1, 1, 1),
      });

      // Uma caixa por carta: união do corte da frente com o do verso. Assim os
      // ticks só marcam o contorno externo — a dobra (frente/verso) não é corte.
      const frenteBox = trimBoxPt(slot, frontTrim);
      const versoBox = trimBoxPt(slot, backTrim);
      boxes.push({
        left: Math.min(frenteBox.left, versoBox.left),
        right: Math.max(frenteBox.right, versoBox.right),
        bottom: Math.min(frenteBox.bottom, versoBox.bottom),
        top: Math.max(frenteBox.top, versoBox.top),
      });
    }

    drawCutMarksForBoxes(page, boxes, {
      tickLen: ART_TICK_LEN,
      outset: CUT_MARK_OUTSET,
    });
    page.drawPage(overlayPages[pageIndex], {
      x: 0,
      y: 0,
      width: PAGE.width,
      height: PAGE.height,
    });
  }
};

/**
 * @param {ArrayBuffer|Uint8Array} overlayBytes — PDF só com texto/QR
 * @param {Array<{ bucket: string }>} pageCards — cartas na mesma ordem do overlay
 * @param {{ paper?: string, geometry?: object, resolveArtUrl?: (artKey: string) => Promise<string|null> }} [options]
 *        `paper: "art"` usa composeArtSheet; `resolveArtUrl` reassina a URL da arte.
 */
const composeSheetWithUnits = async (
  overlayBytes,
  pageCards,
  { paper = "majorca", geometry, resolveArtUrl } = {}
) => {
  const overlayDoc = await PDFDocument.load(overlayBytes);
  const out = await PDFDocument.create();
  const overlayPages = await out.embedPdf(overlayDoc, overlayDoc.getPageIndices());

  if (paper === "art") {
    await composeArtSheet(out, overlayPages, pageCards, geometry, resolveArtUrl);
    return out.save();
  }

  const unitCache = new Map();
  const embedUnit = async (bucket) => {
    if (unitCache.has(bucket)) return unitCache.get(bucket);
    const bytes = await fetchUnitTemplateBytes(bucket);
    const [embedded] = await out.embedPdf(await PDFDocument.load(bytes), [0]);
    unitCache.set(bucket, embedded);
    return embedded;
  };

  for (let pageIndex = 0; pageIndex < overlayPages.length; pageIndex += 1) {
    const page = out.addPage([PAGE.width, PAGE.height]);
    const start = pageIndex * 10;
    const cardsOnPage = pageCards.slice(start, start + 10);
    const occupiedSlots = cardsOnPage.map((_, i) => slotForIndex(i));

    for (let i = 0; i < cardsOnPage.length; i += 1) {
      const card = cardsOnPage[i];
      const unit = await embedUnit(card.bucket || "majorca");
      const slot = occupiedSlots[i];
      const { x, y } = slotBottomLeft(slot);
      clipRect(page, x, y, ARTWORK_LAYOUT.width, ARTWORK_LAYOUT.height);
      page.drawPage(unit, {
        x,
        y,
        width: ARTWORK_LAYOUT.width,
        height: ARTWORK_LAYOUT.height,
      });
      page.pushOperators(popGraphicsState());
    }

    // Depois das molduras, antes do texto: ticks de corte nos slots ocupados.
    drawCutMarks(page, occupiedSlots);

    page.drawPage(overlayPages[pageIndex], {
      x: 0,
      y: 0,
      width: PAGE.width,
      height: PAGE.height,
    });
  }

  return out.save();
};

export { composeSheetWithUnits, slotBottomLeft };
